import { Checkbox, Image } from '@smartgift/frontend-toolkit';
import { difference } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterOptions } from '@hero/components/molecules/Table/Filter/context';
import { TableSelectFilter, Table } from '@hero/molecules';
import { formatCurrency } from '@hero/utils';

import { getSelectedFiltersBySkus } from './helpers';
import * as S from './style';

const FilterContext = React.createContext();

function Filter({ onFilterChange, globalFilters, portalTargetRef }) {
  const { t } = useTranslation();

  return (
    <>
      <FilterContext.Consumer>
        {({ product }) => {
          return product?.attrList?.map(attr => {
            return (
              <FilterOptions.Provider
                key={attr.name}
                value={{ inputPlaceholderOnMenu: t('searchAnAttr') }}
              >
                <TableSelectFilter
                  options={attr.values.map(value => ({ label: value, value }))}
                  onChange={value => {
                    onFilterChange({
                      ...globalFilters.filters,
                      [attr.name]: value
                    });
                  }}
                  selectedOption={globalFilters.filters[attr.name]}
                  placeholder={attr.name}
                  controlWidth={100}
                  menuPosition="absolute"
                  menuPortalTarget={portalTargetRef.current}
                  styles={{
                    menuPortal: () => ({
                      top: 40,
                      left: 16
                    })
                  }}
                  creatable={false}
                />
              </FilterOptions.Provider>
            );
          });
        }}
      </FilterContext.Consumer>
    </>
  );
}

Filter.propTypes = {
  onFilterChange: PropTypes.func,
  globalFilters: PropTypes.object,
  portalTargetRef: PropTypes.object
};

function SkuSelectionTable({ product, selectedSkus, setSelectedSkus }) {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 6
  });
  const [skipGlobalFilterSet, setSkipGlobalFilterSet] = React.useState(true);

  const allAttrToFilter = product?.attrList?.reduce(
    (result, attr) => ({
      ...result,
      [attr.name]: attr?.values?.map(value => ({ label: value, value }))
    }),
    {}
  );

  const [rowSelection, setRowSelection] = React.useState(
    selectedSkus?.reduce((result, sku) => ({ ...result, [sku.code]: true }), {})
  );

  const [globalFilters, setGlobalFilters] = React.useState({
    filters: {
      searchTerm: '',
      ...(selectedSkus?.length
        ? getSelectedFiltersBySkus(selectedSkus, allAttrToFilter)
        : allAttrToFilter)
    }
  });

  const columns = React.useMemo(
    () => [
      {
        id: 'select',
        maxSize: 30,
        header: ({ table }) => (
          <Checkbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler()
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler()
            }}
          />
        )
      },
      {
        header: t('product'),
        accessorKey: 'images',
        size: 90,
        cell: ({ row }) => {
          return (
            <S.ImageWrapper>
              <Image src={row.original.images?.[0].url} width="100%" height="100%" />
            </S.ImageWrapper>
          );
        }
      },
      {
        id: 'variant',
        header: t('variant'),
        size: 280,
        accessorKey: 'name',
        cell: ({ row }) => {
          return (
            row?.original?.name +
            ' / ' +
            Object.keys(row?.original?.attrs || {})
              .map(attr => row?.original?.attrs[attr])
              .join(' | ')
          );
        }
      },
      {
        id: 'price',
        header: t('price'),
        accessorKey: 'price',
        cell: props => {
          return <b>{formatCurrency(props.getValue())}</b>;
        }
      }
    ],
    []
  );

  useEffect(() => {
    setSelectedSkus(product.skus.filter(sku => rowSelection[sku?.sku]));
  }, [rowSelection]);

  const tableRef = useRef(null);

  const filteredSkus = useMemo(() => {
    if (globalFilters.searchTerm) {
      return product.skus.filter(sku =>
        sku.name?.toLowerCase().includes(globalFilters.searchTerm?.toLowerCase())
      );
    }

    return product.skus;
  }, [globalFilters.searchTerm]);

  return (
    <FilterContext.Provider value={{ product }}>
      <S.SkuTableDescription variant="body2">{t('skuTableDescription')}</S.SkuTableDescription>
      <Table
        tableRef={tableRef}
        columns={columns}
        data={filteredSkus}
        globalFilters={globalFilters}
        setGlobalFilters={getGlobalFilters => {
          const newGlobalFilters = getGlobalFilters(globalFilters);
          let action = '';
          let filterKey = '';

          Object.keys(newGlobalFilters.filters).forEach(key => {
            const oldF = globalFilters.filters[key];
            const newF = newGlobalFilters.filters[key];

            let diff = difference(newF, oldF);
            if (diff.length) {
              action = 'added';
              filterKey = key;
            }

            diff = difference(oldF, newF);
            if (diff.length) {
              action = 'removed';
              filterKey = key;
            }
          });

          if (action === 'added') {
            const allF = allAttrToFilter[filterKey];
            const newF = newGlobalFilters.filters[filterKey];

            if (allF.length === newF.length) {
              newGlobalFilters.filters = allAttrToFilter;
            }
          }

          if (
            action === 'removed' &&
            globalFilters.filters[filterKey].length === allAttrToFilter[filterKey].length
          ) {
            Object.keys(newGlobalFilters.filters).forEach(key => {
              if (key !== filterKey) {
                newGlobalFilters.filters[key] = [];
              }
            });
          }

          if (!skipGlobalFilterSet) {
            const selectedSkus = product?.skus?.filter(sku => {
              const a = Object.keys(sku?.attrs || {}).filter(attrKey => {
                const attr = sku.attrs[attrKey];
                return !!newGlobalFilters.filters[attrKey]?.find(filter => filter.value === attr);
              });

              return a.length > 0;
            });

            const newRowSelection = selectedSkus.reduce(
              (result, sku) => ({ ...result, [sku.code]: true }),
              {}
            );
            setRowSelection(newRowSelection);
          } else {
            setSkipGlobalFilterSet(false);
          }

          setGlobalFilters(newGlobalFilters);
        }}
        rowSelection={rowSelection}
        pagination={{
          pageIndex,
          pageSize,
          total: product.skus.length
        }}
        manualPagination
        setPagination={setPagination}
        setRowSelection={getRowSelection => {
          const newRowSelection = getRowSelection(rowSelection);
          // const selectionArray = Object.entries(rowSelection).map(rowArray => rowArray[0]);
          const newSelectionArray = Object.entries(newRowSelection).map(rowArray => rowArray[0]);

          // let diff = null;
          // let action = '';
          // diff = difference(newSelectionArray, selectionArray);

          // if (diff.length) {
          //   action = 'added';
          // } else {
          //   diff = difference(selectionArray, newSelectionArray);
          //   action = 'removed';
          // }

          if (newSelectionArray.length === product.skus.length) {
            setGlobalFilters({
              filters: allAttrToFilter
            });
          }
          if (newSelectionArray.length === 0) {
            Object.keys(globalFilters.filters).forEach(key => {
              globalFilters.filters[key] = [];
            });

            setGlobalFilters({
              ...globalFilters,
              filters: globalFilters.filters
            });
          }

          setRowSelection(newRowSelection);
        }}
        enabled={{ filter: true, header: true, perPageSelection: true, search: true }}
        components={{
          SelectionHead: () => null,
          SecondHead: () => null,
          Filter: props => {
            return <Filter {...props} portalTargetRef={tableRef} />;
          }
        }}
        getRowId={row => {
          return row.sku;
        }}
      />
    </FilterContext.Provider>
  );
}

export { SkuSelectionTable };

SkuSelectionTable.propTypes = {
  product: PropTypes.object,
  selectedSkus: PropTypes.array,
  setSelectedSkus: PropTypes.func
};
